import { generateDynamicPath } from 'common/utils/routing';
import { isAccinCommunity } from 'community/utils/isAccinCommunity';

import { NatalieRoutes } from './routes';

/**
 * Build a Natalie route path while preserving the current URL query — notably
 * `?testInviteCode=`, the unified Natalie test signal.
 *
 * `generateDynamicPath`'s `queryParams` arg builds a *fresh* query from the
 * passed object only; it does not read or merge `location.search`. This helper
 * wraps it so every Natalie navigation carries the existing query forward
 * (caller-provided params merged on top, overriding any same-named key), so a
 * navigation cannot silently drop the test signal. Pure/string-level — no
 * router coupling — so it is trivially testable and usable from both
 * `history.push` call sites and redux `push` effects.
 *
 * @param route        a Natalie route template (e.g. `NatalieRoutes.AccountlessJoin`)
 * @param variables    path params to interpolate into the template
 * @param currentSearch the current `location.search` (with or without a leading `?`)
 * @param extraParams  query params to merge on top of the preserved query
 * @param options      `{ isAbsolute: true }` to prefix the origin (shareable links)
 */
export const buildNataliePath = (
  route: string,
  variables: Record<string, unknown> = {},
  currentSearch = '',
  extraParams: Record<string, string> = {},
  options?: { isAbsolute?: boolean }
): string => {
  const path = generateDynamicPath(route, variables, undefined, options?.isAbsolute ? { isAbsolute: true } : undefined);

  const params = new URLSearchParams(currentSearch);
  Object.entries(extraParams).forEach(([key, value]) => params.set(key, value));

  const query = params.toString();
  return query ? `${path}?${query}` : path;
};

/**
 * Resolve where a registered participant goes once registration has landed: the
 * mic check, or the pre-join step for communities that use one.
 *
 * Two callers need this choice — the register-success effect and the public
 * landing, which hands the same path to the profile step as its return path — so
 * it lives here rather than inline in either. A second copy of the
 * `isAccinCommunity` branch is the drift risk this exists to remove.
 *
 * @param eventId       the Natalie event being joined
 * @param communityId   the event's community; an unresolved event falls to the join step
 * @param currentSearch the current `location.search`, preserved onto the path
 */
export const buildPostRegistrationPath = (
  eventId: string,
  communityId: string | undefined,
  currentSearch = ''
): string => {
  const route = communityId && isAccinCommunity(communityId) ? NatalieRoutes.BeforeJoin : NatalieRoutes.Join;

  return buildNataliePath(route, { id: eventId }, currentSearch);
};
